import React from "react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Grid, Typography } from "@mui/material";
import CourseActivityIcon from "../Icons/CourseActivityIcon";
import useMediaQuery from "@mui/material/useMediaQuery";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { activityTypes } from "../../constants";
import Skeleton from "../Skeleton/Skeleton";

function toFullDate(dt) {
  if (!dt) return null;

  const _dt = new Date(dt);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit"
  }).format(_dt);
}

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(4.5),
    paddingBottom: theme.spacing(5.5),
    borderBottom: `solid 1px ${theme.palette.secondary[300]}`,
    width: "100%"
  },
  subtitleDivider: {
    padding: theme.spacing(0, 1, 0, 1),
    [theme.breakpoints.down("lg")]: {
      display: "none"
    }
  },
  title: {
    wordBreak: "break-word"
  }
}));

const ContentViewerLoadingSkeleton = () => {
  return (
    <Box display="flex" flexDirection="column" flex="0 1 50%">
      <Box display="flex" alignItems="center">
        <Skeleton loading variant="circular" height={40} minWidth={40} mr={2} />
        <Skeleton loading width="100%" height={28} />
      </Box>
      <Skeleton loading width="70%" height={20} ml={7} mt={0.5} />
    </Box>
  );
};
function ContentViewerHeader(props) {
  const { activityTitle, navigationButtons, contentType, isLoading } = props;
  const classes = useStyles();
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();

  function getTitle() {
    if (contentType === activityTypes.survey) {
      return `${t(
        "contentViewer.contentViewerHeader.survey"
      )}: ${activityTitle}`;
    } else if (contentType === activityTypes.quiz) {
      return `${t(
        "contentViewer.contentViewerHeader.assessment"
      )}: ${activityTitle}`;
    } else if (contentType === activityTypes.homework) {
      return `${t(
        "contentViewer.contentViewerHeader.homework"
      )}: ${activityTitle}`;
    } else {
      return activityTitle;
    }
  }

  function getIcon() {
    if (contentType) {
      return <CourseActivityIcon type={contentType} />;
    }
  }

  function getSubtitle() {
    let subtitle = [];
    if (props.subtitle) {
      subtitle.push(
        <Typography variant="body2" key="0">
          {props.subtitle}
        </Typography>
      );
      subtitle.push(
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.subtitleDivider}
          key="1"
        >
          |
        </Typography>
      );
    }
    subtitle.push(
      <Typography variant="body2" color="textSecondary" key="2">
        {t("contentViewer.contentViewerHeader.lastVisited") +
          ": " +
          (toFullDate(props.lastVisited) ||
            t("contentViewer.contentViewerHeader.never"))}
      </Typography>
    );
    return subtitle;
  }

  return (
    <div className={classes.root}>
      <Grid
        container
        spacing={0}
        wrap={smallScreen ? "wrap" : "nowrap"}
        sx={{ justifyContent: "space-between" }}
      >
        {isLoading ? (
          <ContentViewerLoadingSkeleton />
        ) : (
          <Grid container item xs={12} sm={9}>
            <Box
              display="flex"
              flexDirection="column"
              color={theme.palette.text.secondary}
            >
              <Box display="flex" pr={4} pt={0.75}>
                <Box pt={0.5} pr={3}>
                  {getIcon()}
                </Box>
                <Typography variant="h4" align="left" className={classes.title}>
                  {getTitle()}
                </Typography>
              </Box>
              <Box
                pt={0.5}
                pl={6.25}
                display="flex"
                flexDirection={{ xs: "column", lg: "row" }}
              >
                {getSubtitle()}
              </Box>
            </Box>
          </Grid>
        )}
        <Box pl={{ xs: 6, md: 0 }} pt={{ xs: 4, md: 0 }}>
          {navigationButtons}
        </Box>
      </Grid>
    </div>
  );
}

export default ContentViewerHeader;

ContentViewerHeader.propTypes = {
  contentType: PropTypes.string,
  lastVisited: PropTypes.string,
  activityTitle: PropTypes.string,
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};
